import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, ChevronRight, Plus, X } from 'lucide-react';
import { useSavedViews } from '../../contexts/SavedViewsContext';
import { toast } from 'sonner';
import { safeStorage } from '../../utils/safeStorage';

/**
 * Sidebar "Saved views" disclosure.
 *
 * Renders the user's saved view list inside the existing sidebar. Each item
 * is a click-to-apply route jump. Hover reveals the delete X.
 */
export function SidebarSavedViews({ expanded }) {
  const { views, removeView, addView } = useSavedViews();
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => {
    try { return safeStorage.getString('polkdesq_sidebar_saved_open') !== 'false'; } catch { return true; }
  });
  const [drafting, setDrafting] = useState(false);
  const [draftName, setDraftName] = useState('');

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { safeStorage.setString('polkdesq_sidebar_saved_open', String(next)); } catch { /* storage disabled — collapsed state resets next session */ }
  };

  if (!expanded) {
    // Collapsed sidebar shows only a single icon; full disclosure is off.
    return (
      <div className="px-2 mt-3">
        <button
          data-testid="sidebar-saved-collapsed"
          onClick={() => navigate('/saved-views')}
          className="w-full h-9 rounded-xl flex items-center justify-center"
          style={{ color: 'var(--pd-text-secondary)' }}
          title="Saved views"
          aria-label="Saved views"
        >
          <Bookmark className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const handleAdd = () => {
    const route = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const filters = Object.fromEntries(params.entries());
    // Auto-name fallback so users don't HAVE to type — they can rename inline later.
    const fallback = (() => {
      const slug = route.replace('/', '') || 'today';
      const label = slug.charAt(0).toUpperCase() + slug.slice(1);
      const filterCount = Object.keys(filters).length;
      return filterCount ? `${label} · ${filterCount} filter${filterCount === 1 ? '' : 's'}` : label;
    })();
    const name = draftName.trim() || fallback;
    addView(name, route, filters);
    toast.success('View saved');
    setDraftName('');
    setDrafting(false);
  };

  const applyView = (view) => {
    const qs = view.filters && Object.keys(view.filters).length ? '?' + new URLSearchParams(view.filters).toString() : '';
    navigate(`${view.route}${qs}`);
  };

  return (
    <div className="px-3 mt-3" data-testid="sidebar-saved-section">
      <button
        data-testid="sidebar-saved-toggle"
        onClick={toggle}
        className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: 'var(--pd-text-tertiary)', letterSpacing: '0.08em' }}
      >
        <span className="flex items-center gap-1.5">
          <Bookmark className="w-3 h-3" />
          Saved views {views.length ? `(${views.length})` : ''}
        </span>
        <ChevronRight className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open ? (
        <div className="mt-1 space-y-0.5">
          {views.length === 0 && !drafting ? (
            <div className="px-2 py-1.5 text-[10px]" style={{ color: 'var(--pd-text-tertiary)' }}>
              Save the current filters as a one-click view.
            </div>
          ) : null}

          {views.map((view) => (
            <div key={view.id} data-testid={`sidebar-saved-${view.id}`} className="group flex items-center rounded-lg hover:bg-white/[0.05] transition-colors">
              <button
                onClick={() => applyView(view)}
                className="flex-1 text-left text-[12px] px-2 py-1.5 truncate"
                style={{ color: 'var(--pd-text-secondary)' }}
                title={`${view.route} — ${Object.keys(view.filters || {}).length} filters`}
              >
                {view.name}
              </button>
              <button
                data-testid={`sidebar-saved-remove-${view.id}`}
                onClick={(e) => { e.stopPropagation(); removeView(view.id); }}
                className="opacity-0 group-hover:opacity-100 p-1 mr-1 rounded transition-opacity"
                aria-label={`Delete saved view ${view.name}`}
              >
                <X className="w-3 h-3" style={{ color: 'var(--pd-text-tertiary)' }} />
              </button>
            </div>
          ))}

          {drafting ? (
            <div className="flex items-center gap-1 px-2 py-1">
              <input
                autoFocus
                data-testid="sidebar-saved-input"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
                  if (e.key === 'Escape') { setDrafting(false); setDraftName(''); }
                }}
                onBlur={handleAdd}
                placeholder="Name this view"
                maxLength={60}
                className="flex-1 bg-transparent text-[12px] outline-none border-b"
                style={{ color: 'var(--pd-text-primary)', borderColor: 'var(--pd-focus)' }}
              />
            </div>
          ) : (
            <button
              data-testid="sidebar-saved-add"
              onClick={() => setDrafting(true)}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium hover:bg-white/[0.05] transition-colors"
              style={{ color: 'var(--pd-text-tertiary)' }}
            >
              <Plus className="w-3 h-3" /> Save current view
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default SidebarSavedViews;
